/* eslint-disable prettier/prettier */
import createContainer from 'constate';
import { useState } from 'react';
import _ from './LodashImports';
import {
  addCollection,
  addProduct,
  initCartFromPersistance,
  removeCollection,
  removeProduct,
  setWarrantyAndServices,
} from './cart';
import { nameHOC } from './hocutils';
import { is3pSeller } from './urlHelper';

function useCartContextHook() {
  const [cart, setCart] = useState({
    bundleItems: [],
  });
  const [showCart, setShowCart] = useState(false);
  const [showExtendedWarrantyModal, setShowExtendedWarrantyModal] = useState(false);
  const [pendingProductData, setPendingProductData] = useState(null);
  const [isSizeSelected, setSizeSelection] = useState(false);
  const [isVariantSelectionVisible, setVariantSelectionVisible] = useState(true);
  const [quantityWhileVariantSelectionVisible, setQuantityWhileVariantSelectionVisible] = useState(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [warrantyOptionCurrent, setWarrantyOptionCurrent] = useState(null);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showBuyNowButton, setShowBuyNowButton] = useState(false);

  const originalAddProduct = addProduct({
    cart,
    setCart,
    setShowCart,
    setVariantSelectionVisible,
    setQuantityWhileVariantSelectionVisible,
  });

  const setWarrantyOptionMethod = (payload) => {
    return setWarrantyAndServices({
      ...payload,
      channel: 'warranty',
    });
  };

  const isBuyNowApplicable = (toggles, sellerId) => {
    if (_.get(toggles, 'isBuyNowEnabled', false) && is3pSeller(sellerId)) {
      return !_.get(toggles, 'isCatalystCheckoutFor3P', false);
    }
    return false;
  };

  const checkBuyNowStatus = ({
    DDMRutHash = '',
    DDMAddresses = 'false',
    DDMPayments = 'false',
    isOneClickCheckoutEnabled = false,
    product,
  } = {}) => {
    setShowBuyNowButton(
      isOneClickCheckoutEnabled &&
      Boolean(DDMRutHash) &&
      DDMAddresses === 'true' &&
      DDMPayments === 'true' &&
      !product.isOutOfStock &&
      !(product.warrantyOptions || product.serviceOptions)
    );
  };

  const handleAddProduct = (options) => {
    if (!options.product) {
      return originalAddProduct(options);
    }

    const { product } = options;
    const hasWarrantyOptions = product?.warrantyOptions?.fieldOptions?.length > 0;
    const warrantyPreSelected = product?.selectedWarranty || (warrantyOptionCurrent && warrantyOptionCurrent.selected);

    if (hasWarrantyOptions && !warrantyPreSelected) {
      setPendingProductData(options);
      setShowExtendedWarrantyModal(true);
      return Promise.resolve();
    }

    if (warrantyOptionCurrent && warrantyOptionCurrent.selected && product) {
      product.selectedWarranty = warrantyOptionCurrent;
      product.warrantySkuId = warrantyOptionCurrent.value || warrantyOptionCurrent.variantId;
      product.warrantyOfferingId = warrantyOptionCurrent.offeringId;
    }

    return originalAddProduct(options);
  };

  const continueAfterWarranty = (warrantyOption = null) => {
    if (!pendingProductData) {
      return Promise.resolve();
    }
    if (!warrantyOption) {
      setPendingProductData(null);
      return Promise.resolve();
    }
    return setWarrantyOptionMethod({
      cart,
      setCart,
    })({
      appCtx: pendingProductData.appCtx,
      item: {
        commerceItemId: pendingProductData.product.commerceItemId,
        skuId: warrantyOption.value || warrantyOption.variantId,
      },
      productContext: pendingProductData.product,
      variantId: pendingProductData.product.skuId,
      offeringId: warrantyOption.offeringId,
      totalQuantity: pendingProductData.product.quantity || 1,
    })
      .then(() => {
        setPendingProductData(null);
        return Promise.resolve();
      })
      .catch((error) => {
        setPendingProductData(null);
        return Promise.reject(error);
      });
  };

  return {
    cart: {
      ...cart,
      isSizeSelected,
      setSizeSelection,
      isVariantSelectionVisible,
      setVariantSelectionVisible,
      quantityWhileVariantSelectionVisible,
      setQuantityWhileVariantSelectionVisible,
      selectedSize,
      setSelectedSize,
      selectedOperatorId,
      setSelectedOperatorId,
      initCartFromPersistance: initCartFromPersistance({ cart, setCart }),
      addProduct: handleAddProduct,

      removeProduct: removeProduct({
        cart,
        setCart,
        setShowCart,
      }),
      addCollection: addCollection({
        cart,
        setCart,
        setShowCart,
        setVariantSelectionVisible,
        setQuantityWhileVariantSelectionVisible,
      }),
      removeCollection: removeCollection({
        cart,
        setCart,
        setShowCart,
      }),

      setWarrantyOption: setWarrantyOptionMethod({
        cart,
        setCart,
      }),
      showCart,
      setShowCart,
      warrantyOptionCurrent,
      setWarrantyOptionCurrent,
      showLoginForm,
      setShowLoginForm,
      isBuyNowApplicable,
      checkBuyNowStatus,
      showBuyNowButton,
      showExtendedWarrantyModal,
      setShowExtendedWarrantyModal,
      continueAfterWarranty,
      pendingProductData,
      setPendingProductData,
      originalAddProduct,
    },
  };
}

const [CartProvider, useCart] = createContainer(useCartContextHook);

const withCart = (WrappedComponent) => {
  const { getInitialProps } = WrappedComponent;
  const ComponentWithCartCtx = (props) => {
    const { cart } = useCart();

    return <WrappedComponent {...props} cart={cart} />;
  };
  if (typeof getInitialProps === 'function') {
    ComponentWithCartCtx.getInitialProps = getInitialProps;
  }
  ComponentWithCartCtx.originalName = nameHOC(WrappedComponent);
  ComponentWithCartCtx.displayName = nameHOC(WrappedComponent, 'WithCartContext');

  return ComponentWithCartCtx;
};

export { withCart, CartProvider, useCart };
